/**
 * LITE仮会員 - 既存ユーザsms
 */
import { ref, Ref, reactive, computed, watch, toRefs } from '@vue/composition-api';
import { Route } from 'vue-router';

import { useRouter } from '@berlin-front/common/composables/useRouter';
import { useContext } from '@berlin-front/common/composables/context';
import { BerlinBffContext } from '@services/BerlinBff';
import { useMutation } from 'vue-query';
import { ApiCacheContext } from '@services/ApiCacheContext';
import useErrors from '@composables/useErrors';
import useFakeProgress from '@berlin-front/common/composables/useFakeProgress';
import { ROUTE_NAME } from '@/router/route-name';
import { notNil } from '@berlin-front/common/utils';
import useIsLegacy from '@composables/useIsLegacy';
import { ModalBridgeContext } from '@services/ModalBridgeContext';
import { ATONE_LITE_SMS_DESCRIPTION_MESSAGE, AUTHCODE_RESEND_MESSAGE, UseSmsAuthContextType } from './context';

export const useSmsAuth = (_$route: Route): UseSmsAuthContextType => ({
  useSmsAuth: (_authCodeRef: Ref) => {
    const [isLegacy] = useIsLegacy();
    const { errors, clearAll } = useErrors();
    const { napoleon } = useContext(BerlinBffContext);
    const globals = useContext(ModalBridgeContext);
    const {
      atoneAuthenticateGetResult,
      atoneLiteModalParameter,
      tokens,
      guestSmsAuthenticateNotifyPostResult,
      setGuestSmsAuthenticateNotifyPostResult,
      setGuestSmsAuthenticateVerifyPostResult,
      atoneLiteGuestLoginAccount,
      guestFingerprintPostResult,
      setGuestFingerprintPostResult,
      setNpTokenAndSignature,
    } = useContext(ApiCacheContext);
    const router = useRouter();
    const infoMessages = ref<string[]>([]);

    const {
      mutate: sendSmsVerify,
      isLoading: isVerifyLoading,
      isSuccess: isVerifySucceeded,
      isError: isVerifyError,
      reset: resetSmsVerify,
    } = useMutation(
      (smsToken: string) =>
        napoleon.guestControllerSmsAuthenticateVerify(
          notNil(tokens.value.guestRequestToken),
          notNil(atoneLiteModalParameter.value.pubKey),
          {
            smsToken,
            trackToken: notNil(guestFingerprintPostResult.value?.trackToken),
            userNo: atoneLiteModalParameter.value.payment.userNo,
            isOldParameter: isLegacy.value,
            requestId: globals.requestId,
          },
        ),
      {
        onSuccess({ data }) {
          // ここでtokenをtrustedにマークする。
          setGuestFingerprintPostResult((value) => ({ ...notNil(value), trustedFlag: true }));
          setGuestSmsAuthenticateVerifyPostResult(data);
          if (data.authenticationToken) {
            setNpTokenAndSignature(data.authenticationToken, data.signature);
          }
        },
      },
    );

    const {
      mutate: sendSmsNotify,
      isLoading: isNotifySmsLoading,
      isSuccess: isNotifySucceeded,
      isError: isNotifyError,
      reset: resetSmsNotify,
    } = useMutation(
      () =>
        napoleon.guestControllerSmsAuthenticateNotify(
          notNil(tokens.value.guestRequestToken),
          notNil(atoneLiteModalParameter.value.pubKey),
          { resend: true },
        ),
      {
        onSuccess({ data }) {
          setGuestSmsAuthenticateNotifyPostResult(data);
          infoMessages.value = [AUTHCODE_RESEND_MESSAGE];
        },
      },
    );

    const phoneNumber = computed(() =>
      notNil(
        guestSmsAuthenticateNotifyPostResult.value?.phoneNumber ||
          atoneAuthenticateGetResult.value?.signInId ||
          atoneLiteGuestLoginAccount.value?.phoneNumber ||
          '',
      ),
    );

    const isLoading = computed(() => isVerifyLoading.value || isNotifySmsLoading.value);
    const isEnded = computed(
      () =>
        !isLoading.value && [isNotifyError, isNotifySucceeded, isVerifyError, isVerifySucceeded].some((v) => v.value),
    );
    const isInitialState = computed(() => !isLoading.value && !isEnded.value);

    // 進捗。useMutationの状況に合わせてプログレスを進める。
    const {
      progress,
      completed: progressCompleted,
      isLoading: isLoadingApi,
    } = toRefs(useFakeProgress(5000, isLoading, isEnded));

    const onSubmitVerify = (smsCode: string) => {
      if (progressCompleted.value || isInitialState.value) {
        resetSmsVerify.value?.();
        resetSmsNotify.value?.();
        clearAll();
        infoMessages.value = [];
        sendSmsVerify(smsCode);
      }
    };

    const onSubmitResend = () => {
      if (progressCompleted.value || isInitialState.value) {
        resetSmsVerify.value?.();
        resetSmsNotify.value?.();
        clearAll();
        infoMessages.value = [];
        sendSmsNotify();
      }
    };

    watch([isVerifySucceeded, progressCompleted], () => {
      if (isVerifySucceeded.value && progressCompleted.value) {
        void router.replace({
          name: ROUTE_NAME.ATONE_LITE_LOADING_SETTLEMENT,
        });
      }
    });

    return reactive({
      description: ATONE_LITE_SMS_DESCRIPTION_MESSAGE,
      phoneNumber,
      errorMessages: computed(() => Object.values(errors.value ?? {}).flat()),
      infoMessages,
      progress,
      isLoadingApi,
      onSubmitVerify,
      onSubmitResend,
      backToRegister: undefined,
      backToPrevious: undefined,
    });
  },
});
